import { Link } from "react-router-dom";
import type { CrewMember } from "@/mocks/admin/crew";
import CrewStatusBadge from "@/pages/crew/components/CrewStatusBadge";

type Props = {
  member: CrewMember;
  onEdit: () => void;
};

export default function IdentityCard({ member, onEdit }: Props) {
  const firstName = member.name.split(" ")[0];
  const utilization = Math.min(
    100,
    Math.round((member.weeklyHours / Math.max(1, member.weeklyTarget)) * 100),
  );

  return (
    <div className="bg-white border border-ink-100 rounded-lg overflow-hidden">
      {/* Top dossier band */}
      <div className="bg-ink-900 text-white px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-white/60">
          <i className="ri-shield-user-line"></i>
          Crew Dossier
        </div>
        <span className="font-mono text-[10px] font-bold text-white/80 bg-white/10 px-2 py-0.5 rounded">
          {member.id}
        </span>
      </div>

      {/* Avatar + name */}
      <div className="px-5 pt-6 pb-5 text-center border-b border-dashed border-ink-200">
        <div
          className={`w-24 h-24 rounded-2xl ${member.color} text-white text-3xl font-display font-bold flex items-center justify-center mx-auto ring-4 ring-cream-100`}
        >
          {member.initials}
        </div>
        <h1 className="font-display text-xl font-bold text-ink-900 mt-3 leading-tight">
          {member.name}
        </h1>
        <div className="text-xs text-ink-500 mt-0.5">{member.role}</div>
        <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
          <CrewStatusBadge status={member.status} />
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
            <i className="ri-star-fill"></i>
            {member.rating}
          </span>
        </div>

        {/* Quick actions row */}
        <div className="grid grid-cols-3 gap-1.5 mt-4">
          <a
            href={`tel:${member.phone}`}
            title="Call"
            className="flex flex-col items-center justify-center gap-0.5 py-2 rounded-md border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer"
          >
            <i className="ri-phone-line text-sm"></i>
            <span className="text-[10px] font-bold">Call</span>
          </a>
          <a
            href={`mailto:${member.email}`}
            title="Email"
            className="flex flex-col items-center justify-center gap-0.5 py-2 rounded-md border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer"
          >
            <i className="ri-mail-line text-sm"></i>
            <span className="text-[10px] font-bold">Email</span>
          </a>
          <button
            onClick={onEdit}
            title="Edit profile"
            className="flex flex-col items-center justify-center gap-0.5 py-2 rounded-md bg-ink-900 hover:bg-ink-800 text-white cursor-pointer"
          >
            <i className="ri-edit-2-line text-sm"></i>
            <span className="text-[10px] font-bold">Edit</span>
          </button>
        </div>
      </div>

      {/* Weekly utilization meter */}
      <div className="px-5 py-4 border-b border-dashed border-ink-200">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500">
            Weekly utilization
          </div>
          <div className="text-[11px] font-bold font-mono text-ink-900 tabular-nums">
            {member.weeklyHours}/{member.weeklyTarget}h
          </div>
        </div>
        <div className="h-2 rounded-full bg-cream-100 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              utilization >= 90
                ? "bg-emerald-500"
                : utilization >= 60
                ? "bg-brand-500"
                : "bg-amber-500"
            }`}
            style={{ width: `${utilization}%` }}
          ></div>
        </div>
        <div className="text-[10px] text-ink-400 mt-1.5">
          {utilization}% of weekly target ·{" "}
          <span className="font-semibold text-ink-600">
            ${(member.weeklyHours * member.hourlyRate).toLocaleString()} earned
          </span>
        </div>
      </div>

      {/* Stat ladder */}
      <div className="px-5 py-3 border-b border-dashed border-ink-200">
        <StatRow label="Hourly rate" value={`$${member.hourlyRate}`} suffix="/hr" />
        <StatRow label="Completed moves" value={member.completedMoves} />
        <StatRow label="Hired" value={member.hireDate} />
        <StatRow label="Home base" value={member.city} small />
      </div>

      {/* Contact block */}
      <div className="px-5 py-4 border-b border-dashed border-ink-200">
        <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500 mb-2">
          Contact
        </div>
        <ul className="space-y-2 text-xs">
          <li className="flex items-center gap-2">
            <i className="ri-phone-line text-ink-400 w-4"></i>
            <a
              href={`tel:${member.phone}`}
              className="text-ink-700 hover:text-brand-700 cursor-pointer font-mono"
            >
              {member.phone}
            </a>
          </li>
          <li className="flex items-center gap-2">
            <i className="ri-mail-line text-ink-400 w-4"></i>
            <a
              href={`mailto:${member.email}`}
              className="text-ink-700 hover:text-brand-700 cursor-pointer truncate"
            >
              {member.email}
            </a>
          </li>
          <li className="flex items-center gap-2">
            <i className="ri-map-pin-line text-ink-400 w-4"></i>
            <span className="text-ink-700">{member.city}</span>
          </li>
        </ul>
      </div>

      {/* Skills */}
      <div className="px-5 py-4 border-b border-dashed border-ink-200">
        <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500 mb-2">
          Certifications & skills
        </div>
        <div className="flex flex-wrap gap-1.5">
          {member.skills.map((s) => (
            <span
              key={s}
              className="text-[10px] font-bold px-2 py-0.5 rounded bg-cream-100 text-ink-700 border border-ink-100"
            >
              {s}
            </span>
          ))}
        </div>
      </div>

      {/* Emergency */}
      <div className="px-5 py-4">
        <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500 mb-1.5">
          Emergency contact
        </div>
        <div className="text-sm font-semibold text-ink-900">
          Robert {member.name.split(" ").slice(-1)}
        </div>
        <div className="text-[11px] text-ink-500">Father · (512) 555-0188</div>
      </div>

      {/* Bottom: assigned to */}
      {member.currentProject && (
        <Link
          to={`/projects/${member.currentProject}`}
          className="block bg-ink-900 hover:bg-ink-800 text-white px-5 py-3 transition-colors cursor-pointer"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider font-bold text-white/60">
                Currently dispatched to
              </div>
              <div className="font-mono text-sm font-bold truncate">
                {member.currentProject}
              </div>
            </div>
            <i className="ri-arrow-right-line text-lg"></i>
          </div>
        </Link>
      )}

      {!member.currentProject && (
        <div className="bg-cream-50 px-5 py-3 text-center">
          <div className="text-[11px] text-ink-500">
            <i className="ri-pause-line"></i>{" "}
            {firstName} is not assigned to a job right now.
          </div>
        </div>
      )}
    </div>
  );
}

function StatRow({
  label,
  value,
  suffix,
  small,
}: {
  label: string;
  value: string | number;
  suffix?: string;
  small?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-1.5 text-xs">
      <span className="text-ink-500">{label}</span>
      <span
        className={`font-bold text-ink-900 tabular-nums ${
          small ? "text-[11px] text-ink-700" : ""
        }`}
      >
        {value}
        {suffix && <span className="text-ink-400 font-normal text-[10px] ml-0.5">{suffix}</span>}
      </span>
    </div>
  );
}